import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import WidgetPlaceholder from "./WidgetPlaceholder";

// ─── Types ────────────────────────────────────────────────────────────────────

type SingleBarRow = {
  name: string;
  value: number;
  percentage?: number;
};

type Props = {
  isLoading: boolean;
  title: string;
  data: unknown;
};

// ─── Colors ───────────────────────────────────────────────────────────────────

// Fixed, exact-match category colors (not substring/regex — "Not treated"
// contains "treated", so a substring match against "treated" would
// misfire on it). Matched case/whitespace-insensitively against the
// backend's `name` field.
const CATEGORY_COLORS: Record<string, string> = {
  treated: "#1E3A8A",
  transferred: "#3B82F6",
  "not treated": "#FDBA74",
  "prescriber resolution unknown": "#9CA3AF",
};

// Stack order bottom-to-top. Backend doesn't guarantee row order, so rows are
// re-sorted into this fixed order before charting; anything unrecognized
// (not in CATEGORY_COLORS) is pushed to the end and gets a fallback color.
const CATEGORY_ORDER = Object.keys(CATEGORY_COLORS);
const FALLBACK_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#0D9488"];

const normalizeName = (name: string) => name.trim().toLowerCase();

// ─── Data unwrap ──────────────────────────────────────────────────────────────

// Accepts either the full API response ({ data: rows }) or the bare rows
// array (WidgetRenderer strips the envelope before passing data down).
const unwrap = (raw: unknown): SingleBarRow[] => {
  if (Array.isArray(raw)) return raw as SingleBarRow[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as SingleBarRow[];
  }
  return [];
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function SingleBarWidget({ isLoading, title, data }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const rows = useMemo(() => unwrap(data), [data]);

  const segments = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(normalizeName(a.name));
      const bi = CATEGORY_ORDER.indexOf(normalizeName(b.name));
      return (ai === -1 ? CATEGORY_ORDER.length : ai) -
        (bi === -1 ? CATEGORY_ORDER.length : bi);
    });

    const total = sorted.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

    return sorted.map((r, i) => {
      const value = Number(r.value) || 0;
      const pct = r.percentage ?? (total > 0 ? (value / total) * 100 : 0);
      return {
        name: r.name,
        value,
        pct,
        color:
          CATEGORY_COLORS[normalizeName(r.name)] ??
          FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      };
    });
  }, [rows]);

  // Same container-sizing approach as DonutPieWidget: chart.reflow() can't
  // resolve height:100% this many flex levels deep and falls back to
  // Highcharts' hardcoded 400px default, so measure the wrapper directly.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const resize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const chart = chartRef.current?.chart;
        if (!chart || !el) return;
        chart.setSize(el.clientWidth, el.clientHeight, false);
      });
    };
    const timer = setTimeout(resize, 0);
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isLoading, segments.length]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "column",
        animation: false,
        backgroundColor: "transparent",
        // Let Highcharts auto-size margins from the actual rendered label
        // widths — a fixed left margin was too tight for "100%" and clipped
        // the y-axis labels.
        spacing: [10, 10, 10, 10],
        style: { fontFamily: "inherit" },
      },
      title: { text: "" },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      accessibility: { enabled: false },
      xAxis: {
        categories: [title],
        lineColor: "#E2E8F0",
        labels: { style: { fontSize: "11px", color: "#334155" } },
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 10,
        reversedStacks: false,
        gridLineColor: "#f0f0f0",
        title: { text: undefined },
        labels: {
          style: { fontSize: "11px", color: "#6c757d" },
          formatter() {
            return `${this.value}%`;
          },
        },
      },
      tooltip: {
        useHTML: true,
        formatter(this: Highcharts.Point) {
          const seg = segments.find((s) => s.name === this.series.name);
          return `<div style="padding:2px 4px;">
            <div style="font-size:12px;font-weight:700;margin-bottom:2px;">${this.series.name}</div>
            <div style="font-size:11px;">Count: <b>${(seg?.value ?? 0).toLocaleString()}</b></div>
            <div style="font-size:11px;">Share: <b>${this.y}%</b></div>
          </div>`;
        },
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          animation: false,
          dataLabels: { enabled: false },
          // Cap the bar's width so a single category doesn't stretch to
          // fill the whole plot area — shrinks further on narrow widgets,
          // never grows past this on wide ones.
          maxPointWidth: 80,
        },
      },
      series: segments.map((s) => ({
        type: "column" as const,
        name: s.name,
        data: [Math.round(s.pct * 100) / 100],
        color: s.color,
      })),
    }),
    [segments, title],
  );

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={280} />;
  }

  if (segments.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100"
        style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
      >
        No data available.
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <div className="d-flex flex-wrap gap-3 mb-2 px-1">
        {segments.map((s) => (
          <div
            key={s.name}
            className="d-flex align-items-center gap-1"
            style={{ fontSize: 12 }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span className="text-truncate" style={{ color: "var(--bs-body-color)" }}>
              {s.name} {s.pct.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <div className="flex-fill" style={{ minHeight: 0 }} ref={wrapperRef}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
      </div>
    </div>
  );
}
